import {
  ChangeDetectionStrategy,
  Component,
  output,
  signal,
  inject,
} from '@angular/core';

import { UploadComponent, UploadEvent, UploadException } from '@web/shared/ui';
import { catchError, combineLatest, Observable, tap, throwError } from 'rxjs';
import { MultimediaGridLayoutItemComponent } from '../../components/multimedia-grid-layout-item/multimedia-grid-layout-item.component';
import { MultimediaTileComponent } from '../../../shared/components/multimedia-tile/multimedia-tile.component';
import { blank, Guid } from '@web/utils';
import { HttpErrorService } from '@web/common';
import { AdminMultimediaDataSourceService } from '../../services/admin-multimedia-data-source.service';
import { AdminMultimediaDetails } from '../../models/admin-multimedia-details';
import { MultimediaGridLayoutComponent } from '../../components/multimedia-grid-layout/multimedia-grid-layout.component';

@Component({
  selector: 'app-multimedia-upload',
  imports: [
    UploadComponent,
    MultimediaGridLayoutItemComponent,
    MultimediaTileComponent,
    MultimediaGridLayoutComponent,
  ],
  templateUrl: './multimedia-upload.component.html',
  styleUrl: './multimedia-upload.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultimediaUploadComponent {
  /* prettier-ignore */
  private readonly multimediaDataSource = inject(AdminMultimediaDataSourceService);
  private readonly httpError = inject(HttpErrorService);

  public readonly uploadQueue = signal<File[]>([]);
  public readonly uploaded = signal<AdminMultimediaDetails[]>([]);
  public readonly uploadedEvent = output<Guid[]>();

  public readonly uploadFn = (event: UploadEvent) => this.upload(event);

  protected shouldShowInfo(item: AdminMultimediaDetails): boolean {
    return !item.isImage;
  }

  private upload(event: UploadEvent): Observable<void> {
    const { files } = event;

    this.addToQueue(files);

    return combineLatest(
      files.map((file) =>
        this.multimediaDataSource.upload(file).pipe(
          tap({
            next: (multimedia) => {
              this.removeFromQueue(file);
              this.addToUploaded(multimedia);
              this.uploadedEvent.emit([multimedia.id]);
            },
            error: () => {
              this.removeFromQueue(file);
            },
          }),
          catchError((response) =>
            throwError(
              () =>
                new UploadException(
                  this.httpError.extract(response).multiLineMessages(),
                ),
            ),
          ),
        ),
      ),
    ).pipe(blank());
  }

  private addToUploaded(multimediaToAdd: AdminMultimediaDetails): void {
    this.uploaded.update((multimedia) => [multimediaToAdd, ...multimedia]);
  }

  private addToQueue(filesToAdd: File[]): void {
    this.uploadQueue.update((files) => [...filesToAdd, ...files]);
  }

  private removeFromQueue(fileToRemove: File): void {
    this.uploadQueue.update((result) =>
      result.filter((file) => file !== fileToRemove),
    );
  }
}
